import React, { useState } from 'react';
import { getFullImageUrl, centsToDollars } from '../../utils';
import store from '../../../store';
import { TTOrderItem, TTOrderItemCreate } from '../../../interfaces/order_item';
import moment from 'moment-timezone';
import { SubscriptionDisplayInfo } from './SubscriptionDisplayInfo';
import { message, Spin } from 'antd';
import { LoadingIcon } from '../../components/Spinner';
import axios from 'axios';
import { getAuthHeaders } from '../../../api/utils';
import { apiUrl } from '../../../env';

interface Props {
  menuItem: TTOrderItemCreate | TTOrderItem;
  onClick: () => void;
}

// Helper function to safely get nested properties
const safeGet = <T,>(fn: () => T, defaultValue: T): T => {
  try {
    const value = fn();
    return value === undefined || value === null ? defaultValue : value;
  } catch {
    return defaultValue;
  }
};

export const getPrice = (
  menuItem: TTOrderItemCreate | TTOrderItem,
  pairedItemTotal: number,
): number => {
  const modifierGroupsTotal = safeGet(
    () =>
      menuItem.modifier_groups.reduce((total, mod) => {
        const optionsTotal =
          mod.modifier_options?.reduce(
            (prev, curr) => prev + (curr.price || 0),
            0,
          ) || 0;
        return total + optionsTotal;
      }, 0),
    0,
  );

  return menuItem.base_price + modifierGroupsTotal + pairedItemTotal;
};

export const getModifierPairingTotal = (
  menuItem: TTOrderItem | TTOrderItemCreate,
): number => {
  if (!menuItem?.modifier_groups) return 0;

  const state = store.getState();
  const isOrderItem = 'id' in menuItem;

  const getPairedItems = (item: TTOrderItem | TTOrderItemCreate) => {
    if (isOrderItem) {
      // Handle order items
      return (
        state.order.order?.order_tabs
          ?.flatMap(tab => tab.order_items)
          .filter(
            o =>
              o?.as_pairing &&
              o?.paired_with &&
              o.paired_with.paired_menu_item_id === item.menu_item_id &&
              o.paired_with.cart_insert_ts === item.cart_insert_ts,
          ) || []
      );
    } else {
      // Handle cart items
      return (
        state.cart.order_create?.order_items.filter(
          o =>
            o?.paired_with &&
            o.paired_with.paired_menu_item_id === item.menu_item_id &&
            o.paired_with.cart_insert_ts === item.cart_insert_ts,
        ) || []
      );
    }
  };

  return menuItem.modifier_groups.reduce((groupTotal, group) => {
    if (!group.modifier_pairing_options) return groupTotal;

    return (
      groupTotal +
      group.modifier_pairing_options.reduce((optionTotal, option) => {
        const pairedItems = getPairedItems(menuItem);
        const pairedItem = pairedItems.find(item =>
          isOrderItem
            ? item.paired_with.pairing_option_id === option.pairing_option_id
            : item.menu_item_id === option.menu_item_id,
        );

        if (!pairedItem) return optionTotal;

        const itemTotal =
          (pairedItem.base_price + getModifierPairingTotal(pairedItem)) *
          (pairedItem.quantity || 1);

        return optionTotal + itemTotal;
      }, 0)
    );
  }, 0);
};

const DishCard: React.FC<Props> = React.memo(({ menuItem, onClick }) => {
  const [loading, setLoading] = useState(false);
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  const pairedItemTotal = getModifierPairingTotal(menuItem);

  const formatBookingTime = (startTime?: string, endTime?: string): string => {
    if (!startTime) return '';

    const start = moment.utc(startTime).tz(timezone).format('MM/D/YY, h:mm a');
    if (!endTime) return start;

    const end = moment.utc(endTime).tz(timezone).format('h:mm a');
    return `${start}-${end}`;
  };

  const handlePortal = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent onClick
    if (!menuItem?.subscription_info?.subscription_id) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `${apiUrl}/api/v1/subscriptions/guest-portal-session`,
        {
          subscription_id: menuItem.subscription_info.subscription_id,
          guest_id: store.getState().auth.userProfile?.id,
        },
        {
          headers: await getAuthHeaders(),
        },
      );

      window.open(response.data.url, '_blank');
    } catch (err) {
      const error = err as any;
      message.error(
        error?.response?.data?.detail[0]?.msg ??
          error?.response?.data?.detail ??
          'Unable to continue...',
      );
    } finally {
      setLoading(false);
    }
  };

  const renderModifierOptions = () => {
    const regularModifiers =
      menuItem.modifier_groups
        ?.filter(m => !m.is_pairing_type)
        ?.flatMap(mod => mod.modifier_options?.map(op => op.name) ?? [])
        ?.filter(Boolean)
        ?.join(', ') ?? '';

    const pairingModifiers =
      menuItem.modifier_groups
        ?.filter(m => m.is_pairing_type)
        ?.flatMap(mod => {
          if ('id' in menuItem) {
            const orderTabs = store.getState()?.order?.order?.order_tabs ?? [];
            const orderItems = orderTabs
              .flatMap(tab => tab?.order_items ?? [])
              .filter(
                o =>
                  o?.as_pairing &&
                  o?.paired_with?.paired_menu_item_id ===
                    menuItem.menu_item_id &&
                  o?.paired_with?.cart_insert_ts === menuItem.cart_insert_ts,
              );
            return orderItems?.map(item => item.name) ?? [];
          }
          return (
            mod.modifier_pairing_options
              ?.map(op => op?.orderItem?.name)
              ?.filter(Boolean) ?? []
          );
        })
        ?.join(', ') ?? '';

    return (
      <>
        {regularModifiers && (
          <p className="text-on-surface/80 text-xs">
            Option(s): {regularModifiers}
          </p>
        )}
        {pairingModifiers && (
          <p className="text-gray-700 text-xs">
            Pairing Option(s): {pairingModifiers}
          </p>
        )}
      </>
    );
  };

  return (
    <tr onClick={onClick}>
      <td>
        <div className="py-2 pr-4 pl-4 rounded">
          <div className="flex flex-row bg-primary/10 p-4 items-start rounded-lg shadow-md justify-between">
            <div className="flex flex-col justify-between leading-normal mr-1">
              <div className="mb-2">
                <div className="flex flex-row space-x-2 items-center mb-2">
                  <div className="text-on-surface font-bold text-sm">
                    {menuItem.name}
                  </div>

                  {'id' in menuItem &&
                    menuItem?.product_type === 'subscription' && (
                      <Spin spinning={loading} indicator={LoadingIcon}>
                        <button
                          className="px-2 flex-0 py-1 hover:cursor-pointer bg-primary-container text-on-primary-container rounded-full text-center text-md font-semibold"
                          onClick={handlePortal}
                        >
                          Manage Subscription
                        </button>
                      </Spin>
                    )}
                </div>

                {menuItem?.product_type === 'booking' && menuItem.booking_info && (
                  <div className="flex items-center flex-row space-x-2 mt-2">
                    <div className="text-sm">
                      <p className="text-on-surface leading-none mb-1">
                        Booking slot:{' '}
                        {['pending', 'pending_vendor_accept'].includes(
                          menuItem.booking_info.status,
                        )
                          ? formatBookingTime(
                              menuItem.booking_info.proposed_start_at,
                              menuItem.booking_info.proposed_end_at,
                            )
                          : formatBookingTime(menuItem.booking_info.booked_for)}
                      </p>
                    </div>
                  </div>
                )}

                {renderModifierOptions()}
              </div>

              <div className="flex items-center flex-row space-x-2">
                {menuItem.product_type !== 'subscription' ? (
                  <div className="text-sm">
                    <p className="text-on-surface leading-none">
                      x {menuItem.quantity}
                    </p>
                  </div>
                ) : (
                  <SubscriptionDisplayInfo orderItem={menuItem} />
                )}

                {getPrice(menuItem, pairedItemTotal) > 0 && (
                  <div className="text-sm">
                    <p className="text-on-surface leading-none">
                      {centsToDollars(getPrice(menuItem, pairedItemTotal))}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {menuItem.image_url && (
              <img
                className="w-24 h-24 rounded object-cover"
                src={getFullImageUrl(
                  store.getState().restaurant.profile.restaurant_id,
                  menuItem.image_url,
                )}
                alt={`${menuItem.name} photo`}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
});

export default DishCard;
